// src/lib/hooks/useGoogleMaps.ts
import { Loader } from "@googlemaps/js-api-loader";
import { useEffect, useState } from "react";

// Types
interface GoogleMapsState {
  isLoaded: boolean;
  isLoading: boolean;
  error: Error | null;
}

// Singleton loader instance
let loaderInstance: Loader | null = null;

const getLoader = () => {
  if (!process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY) {
    throw new Error("Google Maps API key is not configured");
  }

  if (!loaderInstance) {
    loaderInstance = new Loader({
      apiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY,
      libraries: ["places", "drawing", "geometry"],
      version: "weekly",
    });
  }
  return loaderInstance;
};

export function useGoogleMaps() {
  const [state, setState] = useState<GoogleMapsState>({
    isLoaded: false,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    let mounted = true;

    const initializeGoogleMaps = async () => {
      try {
        const loader = getLoader();
        await loader.load();

        if (mounted) {
          setState({
            isLoaded: true,
            isLoading: false,
            error: null,
          });
        }
      } catch (error) {
        if (mounted) {
          setState({
            isLoaded: false,
            isLoading: false,
            error: error instanceof Error ? error : new Error("Failed to load Google Maps"),
          });
        }
      }
    };

    initializeGoogleMaps();

    return () => {
      mounted = false;
    };
  }, []);

  return {
    isGoogleMapsLoaded: state.isLoaded,
    isGoogleMapsLoading: state.isLoading,
    googleMapsError: state.error,
  };
}

// Type guard to check if Google Maps is loaded
export function isGoogleMapsAvailable(): boolean {
  return typeof google !== "undefined" && typeof google.maps !== "undefined";
}
